import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { useAuth } from '@/contexts/AuthContext';
import { openExternalUrl, openMailto } from '@/utils/openExternalUrl';
import { CommunityHostManagementItem } from '@/components/community/menu/CommunityHostManagementItem';
import { CommunityMenuLogout } from '@/components/community/menu/CommunityMenuLogout';
import { CommunityMenuPasswordDialog } from '@/components/community/menu/CommunityMenuPasswordDialog';
import {
  DesktopMenuCard,
  DesktopMenuRow,
  MobileMenuRow,
} from '@/components/community/menu/CommunityMenuRows';

export const CommunityMenuPage: React.FC = () => {
  const navigate = useNavigate();
  const { isCoHost } = useAuth();
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const lpUrl = (import.meta.env.VITE_LP_URL || 'http://localhost:3000').replace(/\/$/, '');

  return (
    <div className="min-h-full bg-[#F8FAFC] flex flex-col items-center justify-start p-0 sm:p-4 text-[#0E1726]">
      <main className="w-full max-w-md sm:rounded-3xl overflow-hidden px-4 space-y-3.5 pt-3 pb-4 md:hidden">
        <div className="bg-white rounded-3xl border border-[#E0E6ED] shadow-2xs overflow-hidden divide-y divide-gray-100">
          <MobileMenuRow
            icon="ph:currency-circle-dollar-bold"
            title="Cashflow Management"
            subtitle="Arus kas, posisi dana & catatan manual"
            onClick={() => navigate('/cash')}
          />
          <MobileMenuRow
            icon="ph:receipt-bold"
            title="Riwayat Transaksi"
            subtitle="Pembayaran booking & mabar"
            onClick={() => navigate('/host/transactions')}
          />
          <MobileMenuRow
            icon="mdi:whatsapp"
            title="WhatsApp Notification"
            subtitle="Hubungkan nomor & pantau pesan otomatis"
            onClick={() => navigate('/host/whatsapp')}
          />
          <CommunityHostManagementItem variant="mobile" />
          {!isCoHost && (
            <MobileMenuRow
              icon="ph:lock-key-bold"
              title="Ganti Password"
              subtitle="Perbarui kata sandi akun"
              onClick={() => setShowPasswordModal(true)}
            />
          )}
          <MobileMenuRow
            icon="ph:shield-check-bold"
            title="Kebijakan Privasi"
            subtitle="Bagaimana data kamu dipakai"
            onClick={() => { void openExternalUrl(`${lpUrl}/privacy-policy`); }}
          />
          <MobileMenuRow
            icon="ph:file-text-bold"
            title="Syarat & Ketentuan"
            subtitle="Aturan penggunaan Sporto"
            onClick={() => { void openExternalUrl(`${lpUrl}/tos`); }}
          />
          <MobileMenuRow
            icon="ph:question-bold"
            title="Bantuan"
            subtitle="FAQ & hubungi tim support"
            onClick={() => openMailto('mailto:hi@sporto.id')}
          />
        </div>
        <CommunityMenuLogout variant="mobile" />
        <div className="text-center py-3 text-xs text-gray-400 font-medium">Sporto v2.1.0</div>
      </main>

      <main className="hidden md:block w-full max-w-5xl mx-auto space-y-6 pb-12">
        <div className="bg-white p-6 rounded-3xl border border-[#E0E6ED] shadow-2xs flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-extrabold text-[#0E1726]">Pengaturan & Menu Komunitas</h1>
            <p className="text-xs text-gray-400 mt-1 font-medium">
              Kelola saldo wallet, keamanan akun, serta akses bantuan aplikasi Sporto.
            </p>
          </div>
          <CommunityMenuLogout variant="desktop" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <DesktopMenuCard icon="ph:wallet-bold" title="Finansial" subtitle="Dompet & pembayaran">
            <DesktopMenuRow
              title="Cashflow Management"
              subtitle="Arus kas, posisi dana & catatan manual"
              onClick={() => navigate('/cash')}
            />
            <DesktopMenuRow
              title="Wallet Komunitas"
              subtitle="Cek saldo & pengajuan penarikan"
              onClick={() => navigate('/host/wallet')}
            />
            <DesktopMenuRow
              title="Riwayat Transaksi"
              subtitle="Catatan transaksi & invoice"
              onClick={() => navigate('/host/transactions')}
            />
            <DesktopMenuRow
              title="WhatsApp Notification"
              subtitle="Hubungkan nomor & pantau pesan otomatis"
              onClick={() => navigate('/host/whatsapp')}
            />
            <CommunityHostManagementItem variant="desktop" />
          </DesktopMenuCard>

          <DesktopMenuCard icon="ph:lock-key-bold" title="Akun & Keamanan" subtitle="Kata sandi & privasi akun">
            {!isCoHost && (
              <DesktopMenuRow
                title="Ganti Password"
                subtitle="Perbarui kata sandi akun komunitas"
                onClick={() => setShowPasswordModal(true)}
              />
            )}
            {!isCoHost && (
              <button
                type="button"
                onClick={() => navigate('/host/profile-settings/delete')}
                className="w-full p-3.5 rounded-2xl border border-red-100 bg-red-50/30 hover:bg-red-50 transition-all flex items-center justify-between text-left cursor-pointer group"
              >
                <div>
                  <h3 className="font-bold text-red-600 text-sm">Hapus Akun Komunitas</h3>
                  <p className="text-xs text-red-400 font-medium mt-0.5">Penutupan akun permanen</p>
                </div>
                <Icon icon="ph:caret-right-bold" className="w-4 h-4 text-red-400 group-hover:text-red-600 transition-colors" />
              </button>
            )}
            {isCoHost && (
              <p className="text-xs text-gray-400 font-medium px-1">
                Co-host tidak dapat mengubah password atau menghapus akun komunitas.
              </p>
            )}
          </DesktopMenuCard>

          <DesktopMenuCard icon="ph:question-bold" title="Bantuan & Legal" subtitle="Bantuan CS & kebijakan platform">
            <DesktopMenuRow
              title="Hubungi Support"
              subtitle="Bantuan email hi@sporto.id"
              onClick={() => openMailto('mailto:hi@sporto.id')}
            />
            <DesktopMenuRow
              title="Kebijakan Privasi & Syarat"
              subtitle="Aturan penggunaan aplikasi"
              onClick={() => { void openExternalUrl(`${lpUrl}/privacy-policy`); }}
              trailingIcon="ph:arrow-square-out-bold"
            />
          </DesktopMenuCard>
        </div>
        <div className="text-center py-4 text-xs text-gray-400 font-medium">Sporto Dashboard v2.1.0</div>
      </main>

      {!isCoHost && (
        <CommunityMenuPasswordDialog open={showPasswordModal} onOpenChange={setShowPasswordModal} />
      )}
    </div>
  );
};

export default CommunityMenuPage;
